import { useEffect, useMemo, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  fetchHabitualWeekdays,
  toggleSetValue,
  weekdayNames,
  weekdaysInWeekOrder,
  type ApiGet,
  type HabitualWeekdaysRead,
} from "@heraion/shared";

import { Button } from "../primitives/Button";
import { ChipMultiRow } from "../primitives/ChipMultiRow";
import { LoadingIndicator } from "../primitives/LoadingIndicator";
import { makeSettingsCardStyles } from "./settingsCardStyles";
import type { UiTheme } from "../theme";

// #508: a tap turns the deduced days into the athlete's own; the mount saves
// `Profile.habitualWeekdays`, undefined = back to the deduction.

/** The deduction, read ONCE at mount: `apiFetch` must be stable (a
 *  module-level fetcher), a new one is never re-read. null while it loads,
 *  "error" if the read failed (never shown as "not enough history"). */
export function useHabitualWeekdaysRead(
  apiFetch: ApiGet,
): HabitualWeekdaysRead | null | "error" {
  const [read, setRead] = useState<HabitualWeekdaysRead | null | "error">(null);
  useEffect(() => {
    let cancelled = false;
    fetchHabitualWeekdays(apiFetch)
      .then((r) => {
        if (!cancelled) setRead(r);
      })
      .catch(() => {
        if (!cancelled) setRead("error");
      });
    return () => {
      cancelled = true;
    };
    // Mount-only: the hosts pass a module-level apiFetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return read;
}

const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

export function HabitualDaysChoice({
  theme,
  locale,
  weekStartDay,
  manual,
  read,
  onSave,
  saving = false,
  error = null,
}: {
  theme: UiTheme;
  locale: string;
  weekStartDay: number;
  /** The athlete's correction; undefined = none. */
  manual: readonly number[] | undefined;
  read: HabitualWeekdaysRead | null | "error";
  /** The new correction; undefined = back to the deduction. */
  onSave: (next: number[] | undefined) => void;
  saving?: boolean;
  error?: string | null;
}) {
  const { t } = useTranslation();
  const cs = useMemo(() => makeSettingsCardStyles(theme), [theme]);
  const local = useMemo(() => makeStyles(theme), [theme]);
  const shortNames = useMemo(() => weekdayNames(locale, 0), [locale]);
  const longNames = useMemo(() => weekdayNames(locale, 0, "long"), [locale]);
  const days = useMemo(() => weekdaysInWeekOrder(ALL_DAYS, weekStartDay), [weekStartDay]);
  const own = manual !== undefined && manual.length > 0;
  const inferred = read !== null && read !== "error" ? read.inferred : null;
  const selected = useMemo(
    () => new Set<number>(own ? manual : (inferred ?? [])),
    [own, manual, inferred],
  );
  const weeks = read !== null && read !== "error" ? read.windowWeeks : null;

  const status = own
    ? t("mobile.prefs.habitualDaysManual", { defaultValue: "Set by you." })
    : read === "error"
      ? t("mobile.prefs.habitualDaysReadError", {
          defaultValue: "Could not read your log. Pick your days, or try again later.",
        })
      : inferred
        ? weeks === null
          ? t("mobile.prefs.habitualDaysInferredNoWindow", {
              defaultValue: "Deduced from your log.",
            })
          : t("mobile.prefs.habitualDaysInferred", {
              count: weeks,
              defaultValue: "Deduced from your last {{count}} weeks.",
            })
        : weeks === null
          ? t("mobile.prefs.habitualDaysUnknownNoWindow", {
              defaultValue: "Not enough training to tell yet. Pick your days.",
            })
          : t("mobile.prefs.habitualDaysUnknown", {
              count: weeks,
              defaultValue: "Not enough training in the last {{count}} weeks to tell. Pick your days.",
            });

  const toggle = (day: number) => {
    const next = toggleSetValue(selected, day);
    if (next.size === 0) return;
    onSave(weekdaysInWeekOrder([...next], weekStartDay));
  };

  const label = t("mobile.prefs.habitualDaysLabel", { defaultValue: "Usual training days" });
  return (
    <View style={cs.card}>
      <Text style={cs.cardLabel}>{label}</Text>
      <Text style={cs.cardHelp}>
        {t("mobile.prefs.habitualDaysHelp", {
          defaultValue: "Used to warn when a program trains the same muscle on back-to-back days.",
        })}
      </Text>
      {read === null && !own ? (
        <LoadingIndicator theme={theme} />
      ) : (
        <>
          <Text style={cs.cardValue}>{status}</Text>
          {error ? (
            // RNW forwards both to the DOM: role="alert", aria-live="polite".
            <Text style={local.error} accessibilityRole="alert" accessibilityLiveRegion="polite">
              {error}
            </Text>
          ) : null}
          <ChipMultiRow<number>
            theme={theme}
            choices={days}
            selected={selected}
            onToggle={toggle}
            label={(d) => shortNames[d] ?? String(d)}
            chipA11yLabel={(d) => longNames[d] ?? String(d)}
            // The last day on cannot go: no usual day is not a correction.
            isDisabled={(d) => saving || (selected.size === 1 && selected.has(d))}
            groupA11yLabel={label}
          />
          {selected.size === 1 ? (
            <Text style={cs.cardHelp}>
              {t("mobile.prefs.habitualDaysLastOn", {
                defaultValue: "At least one day stays on.",
              })}
            </Text>
          ) : null}
          {own ? (
            <Button
              theme={theme}
              variant="secondary"
              label={t("mobile.prefs.habitualDaysUseDeduced", {
                defaultValue: "Use the deduced days",
              })}
              onPress={() => onSave(undefined)}
              disabled={saving}
              loading={saving}
              style={local.action}
            />
          ) : null}
        </>
      )}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  return StyleSheet.create({
    error: { color: theme.palette.danger, fontSize: theme.typography.sizes.base },
    action: { alignSelf: "flex-start" },
  });
}
